import { Link } from "react-router-dom";
import { Leaf, Sprout, HeartHandshake, Mountain, ArrowRight } from "lucide-react";
import { useLang } from "@/App";
import { all, pick, seq } from "@/data/images";
import Hero from "@/components/Hero";
import Foto from "@/components/Foto";
import Testimonios from "@/components/Testimonios";
import { Reveal, Pretitulo, TituloSeccion, BtnPrimario, BtnSecundario } from "@/components/Reveal";

const VALOR_ICONOS = [Leaf, Sprout, HeartHandshake, Mountain];

export default function Home() {
  const { t, L } = useLang();
  const h = t.home;

  const heroFotos = [1, 2, 3].map((n) => pick("home/banner", n)).filter(Boolean);
  const heroFinal = heroFotos.length ? heroFotos : all("home/banner").slice(0, 3);

  return (
    <div data-testid="pagina-home">
      <Hero pre={h.hero.pre} titulo={h.hero.titulo} fotos={heroFinal} testid="hero-home" alto="h-[78vh] min-h-[560px] md:h-[86vh]" />

      {/* 2. Presentación */}
      <section className="py-20 md:py-28" data-testid="seccion-presentacion">
        <div className="mx-auto grid max-w-[1100px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_6fr] lg:gap-20">
          <Reveal className="img-frame aspect-square w-full">
            <Foto src={pick(h.presentacion.foto[0], 0) || all(h.presentacion.foto[0])[0]} alt="" className="h-full w-full" testid="presentacion-foto" />
          </Reveal>
          <Reveal delay={0.12}>
            <Pretitulo center={false}>{h.presentacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{h.presentacion.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{h.presentacion.cuerpo}</p>
          </Reveal>
        </div>
      </section>

      {/* 3. Accesos — mosaico según referencia estructural */}
      <section className="pb-20 md:pb-28" data-testid="seccion-accesos">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10">
          <TituloSeccion pre={h.accesos.pre} titulo={h.accesos.titulo} />
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3">
            {h.accesos.cards.map((c, i) => {
              const forma =
                i === 0
                  ? "col-span-2 aspect-[4/3] lg:col-span-2 lg:row-span-2 lg:aspect-auto"
                  : i === 5
                  ? "col-span-2 aspect-[16/9] lg:col-span-1 lg:aspect-square"
                  : "col-span-1 aspect-square";
              return (
                <Link
                  key={c.titulo}
                  to={L(c.to)}
                  className={`img-frame group relative block overflow-hidden ${forma}`}
                  data-testid={`acceso-${i}`}
                >
                  <Foto
                    src={pick(c.foto[0], 0) || all(c.foto[0])[0]}
                    alt={c.titulo}
                    className="absolute inset-0 h-full w-full"
                  />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-sand">
                    <span className="font-serif text-xl md:text-2xl">{c.titulo}</span>
                    <ArrowRight size={18} className="shrink-0 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Historia — bloque de alto contraste */}
      <section className="bg-ink py-20 md:py-28" data-testid="seccion-historia">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <Pretitulo claro center={false}>{h.historia.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-sand md:text-5xl">{h.historia.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-sand/75">{h.historia.cuerpo}</p>
            <div className="mt-10">
              <BtnPrimario href={L(h.historia.to)} testid="historia-cta">{h.historia.cta}</BtnPrimario>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="img-frame aspect-[4/3] w-full lg:aspect-[3/3.4]">
            <Foto src={pick(h.historia.foto[0], 0) || all(h.historia.foto[0])[0]} alt="" className="h-full w-full" testid="historia-foto" />
          </Reveal>
        </div>
      </section>

      {/* 5. Valores */}
      <section className="py-20 md:py-28" data-testid="seccion-valores">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={h.valores.pre} titulo={h.valores.titulo} />
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 md:gap-8">
            {h.valores.items.map((v, i) => {
              const Icono = VALOR_ICONOS[i % 4];
              return (
                <Reveal key={v.t} delay={i * 0.08} className="text-center" testid={`valor-${i}`}>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-ochre/40 text-terra">
                    <Icono size={24} strokeWidth={1.3} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-ink">{v.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft">{v.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. Información para tu estadía */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="seccion-info-estadia">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <TituloSeccion pre={h.info.pre} titulo={h.info.titulo} />
          <div className="grid gap-6 md:grid-cols-2 md:gap-8">
            {h.info.cards.map((c, i) => (
              <Link key={c.t} to={L(c.to)} className="group" data-testid={`info-card-${i}`}>
                <Reveal delay={i * 0.1} className="img-frame relative aspect-[4/3.2] w-full md:aspect-[4/2.6]">
                  <Foto src={pick(c.foto[0], 0) || all(c.foto[0])[0]} alt={c.t} className="h-full w-full" />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                  <span className="absolute bottom-7 left-7 flex items-center gap-3 text-sand">
                    <span className="font-serif text-2xl md:text-3xl">{c.t}</span>
                    <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Reveal>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Testimonios */}
      <Testimonios />

      {/* 8. Ubicación */}
      <section className="py-20 md:py-28" data-testid="seccion-ubicacion">
        <div className="mx-auto grid max-w-[1300px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_7fr]">
          <Reveal>
            <Pretitulo center={false}>{h.ubicacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{h.ubicacion.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{h.ubicacion.cuerpo}</p>
            <p className="mt-5 text-sm font-semibold text-ink">{h.ubicacion.direccion}</p>
            <div className="mt-9">
              <BtnPrimario href={h.ubicacion.link} testid="ubicacion-como-llegar-btn">{h.ubicacion.btn}</BtnPrimario>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-lineas bg-white p-2 shadow-lg shadow-ink/5" data-testid="ubicacion-mapa">
              <iframe src={h.ubicacion.iframe} width="100%" height="420" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" title="Mapa" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}